import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { ISelectValue } from 'constants/globalTypes'
import {
  addFuelLossNote,
  approveFuelLossWriteOff,
  assignFuelLoss,
  clearFuelLossDetail,
  getFuelLossDetail,
  rejectFuelLossWriteOff,
  updateFuelLossStatus,
} from 'features/fuelManagement/fuelManagementSlice'
import useModalClose from 'hooks/useModalClose'
import { AppDispatch, RootState } from 'store'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { ILossCaseModalProps } from 'types/fuelOps.interface'
import { IStatusPill } from 'types/ui/statusPill.interface'
import {
  FuelLossCaseStatus,
  FuelLossOutcome,
  FuelLossRootCause,
} from 'types/fuelEnums.interface'
import { formatDateWithTime, formatNumbersForDisplay } from 'utils/helpers'
import {
  formatGhs,
  fuelLossCostGhs,
  FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS,
} from '../Shared/fuelFormatters'
import { formatFuelUserName, mapFuelLoss } from '../Shared/fuelApiMappers'
import {
  fuelLossAuditEventLabel,
  fuelLossOutcomes,
  fuelLossRootCauseLabel,
  fuelLossRootCauses,
} from '../fuelData'
import {
  LossApprovalBanner,
  LossCaseGrid,
  LossCaseItem,
  LossCaseValueEm,
  LossClosureWrap,
  LossModalBody,
  LossNoteCard,
  LossScrollList,
  LossSection,
} from './LossCaseModal.styled'

export const CASE_STATUS_META: {
  [k in 'OPEN' | 'PENDING_APPROVAL' | 'CLOSED' | 'UNDER_INVESTIGATION']: {
    tone: IStatusPill['status']
    label: string
  }
} = {
  OPEN: { tone: 'warning', label: 'Open' },
  UNDER_INVESTIGATION: { tone: 'danger', label: 'Under investigation' },
  PENDING_APPROVAL: { tone: 'accent', label: 'Pending approval' },
  CLOSED: { tone: 'neutral', label: 'Closed' },
}

export const OUTCOME_META: {
  [k in 'RECOVERED' | 'WRITTEN_OFF' | 'DISCIPLINARY']: {
    tone: IStatusPill['status']
    label: string
  }
} = {
  RECOVERED: { tone: 'success', label: 'Recovered' },
  WRITTEN_OFF: { tone: 'neutral', label: 'Written off' },
  DISCIPLINARY: { tone: 'danger', label: 'Disciplinary' },
}

const parseUsers = (data: any) =>
  (data?.data || data || []).map((item: any) => ({
    label: [item.firstName, item.lastName].filter(Boolean).join(' ') || item.name || String(item.id),
    value: item.id,
  }))

export const LossCaseModal = ({ lossId, closeModal, onChanged }: ILossCaseModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const detail = useSelector((s: RootState) => s.fuelManagement.fuelLossDetail)
  useModalClose(closeModal)

  const [owner, setOwner] = useState<ISelectValue | null>(null)
  const [noteText, setNoteText] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [outcome, setOutcome] = useState<ISelectValue | null>(null)
  const [rootCause, setRootCause] = useState<ISelectValue | null>(null)
  const [closureNote, setClosureNote] = useState('')
  const [busy, setBusy] = useState(false)

  const refresh = async () => {
    await dispatch(getFuelLossDetail(lossId) as any)
  }

  useEffect(() => {
    dispatch(getFuelLossDetail(lossId) as any)
    return () => {
      dispatch(clearFuelLossDetail())
    }
  }, [lossId])

  const lossApi = detail?.loss
  const loss = lossApi ? mapFuelLoss(lossApi) : null
  const caseStatus = loss?.caseStatus || 'OPEN'
  const costGhs = loss?.costGhs ?? (loss ? fuelLossCostGhs(loss.liters) : 0)
  const threshold = loss?.approval?.requiredAboveGhs ?? FUEL_WRITE_OFF_APPROVAL_THRESHOLD_GHS
  const approvalRequired = Boolean(loss?.approval)
  const approved = Boolean(loss?.approval?.approvedBy)
  const recovered = loss?.outcome === 'RECOVERED'
  const isClosed = caseStatus === 'CLOSED'

  const afterAction = async () => {
    await refresh()
    onChanged()
  }

  const run = async (fn: () => Promise<any>) => {
    setBusy(true)
    try {
      const resp = await fn()
      if (resp?.meta?.requestStatus === 'fulfilled') {
        await afterAction()
      }
    } finally {
      setBusy(false)
    }
  }

  const handleAssign = () => {
    if (!owner?.value) return
    run(() =>
      dispatch(
        assignFuelLoss({
          id: lossId,
          body: { ownerUserId: Number(owner.value) },
        }) as any,
      ),
    )
  }

  const handleAddNote = () => {
    if (!noteText.trim()) return
    run(async () => {
      const resp = await dispatch(
        addFuelLossNote({ id: lossId, body: { note: noteText.trim() } }) as any,
      )
      if (resp?.meta?.requestStatus === 'fulfilled') setNoteText('')
      return resp
    })
  }

  const handleClose = (targetOutcome?: number) => {
    const outcomeValue = targetOutcome ?? Number(outcome?.value) ?? lossApi?.outcome
    const rootCauseValue = Number(rootCause?.value) || lossApi?.rootCause
    if (!outcomeValue || !rootCauseValue) return
    run(() =>
      dispatch(
        updateFuelLossStatus({
          id: lossId,
          body: {
            caseStatus: FuelLossCaseStatus.Closed,
            outcome: outcomeValue as FuelLossOutcome,
            rootCause: rootCauseValue as FuelLossRootCause,
            note: closureNote.trim() || undefined,
          },
        }) as any,
      ),
    )
  }

  const handleSendForApproval = () => {
    const rootCauseValue = Number(rootCause?.value) || lossApi?.rootCause
    if (!rootCauseValue) return
    run(() =>
      dispatch(
        updateFuelLossStatus({
          id: lossId,
          body: {
            caseStatus: FuelLossCaseStatus.PendingApproval,
            outcome: FuelLossOutcome.WrittenOff,
            rootCause: rootCauseValue as FuelLossRootCause,
            note: closureNote.trim() || undefined,
          },
        }) as any,
      ),
    )
  }

  const handleApprove = () => {
    run(() => dispatch(approveFuelLossWriteOff(lossId) as any))
  }

  const handleReject = () => {
    if (!rejectReason.trim()) return
    run(async () => {
      const resp = await dispatch(
        rejectFuelLossWriteOff({
          id: lossId,
          body: { reason: rejectReason.trim() },
        }) as any,
      )
      if (resp?.meta?.requestStatus === 'fulfilled') setRejectReason('')
      return resp
    })
  }

  if (!loss || !lossApi) {
    return (
      <Modal black>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center>
            <TitleText weight='bold'>Loading loss case…</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
        </Flex>
      </Modal>
    )
  }

  return (
    <Modal black>
      <Flex column gap='0.5rem' width='100%'>
        <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
          <TitleText weight='bold'>{`Loss case — ${loss.dateLabel} · ${loss.tank}`}</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <LossModalBody>
          <Flex center gap='0.5rem' wrap>
            <StatusPill
              status={loss.severity === 'critical' ? 'danger' : 'warning'}
              dot={loss.severity === 'critical'}
            >
              {loss.severity === 'critical' ? 'Critical' : 'Warning'}
            </StatusPill>
            <StatusPill status={CASE_STATUS_META[caseStatus].tone}>
              {CASE_STATUS_META[caseStatus].label}
            </StatusPill>
            {loss.outcome && (
              <StatusPill status={OUTCOME_META[loss.outcome].tone}>
                {OUTCOME_META[loss.outcome].label}
              </StatusPill>
            )}
          </Flex>

          <LossCaseGrid>
            <LossCaseItem>
              <OtherText grey>OPENED</OtherText>
              <LossCaseValueEm>{loss.dateLabel}</LossCaseValueEm>
            </LossCaseItem>
            <LossCaseItem>
              <OtherText grey>AMOUNT</OtherText>
              <LossCaseValueEm>{`${formatNumbersForDisplay(loss.liters)} L`}</LossCaseValueEm>
            </LossCaseItem>
            <LossCaseItem>
              <OtherText grey>COST AT WEIGHTED AVERAGE</OtherText>
              <LossCaseValueEm $color={recovered ? tokens.status.success.fg : undefined}>
                {recovered ? 'Recovered — no cost' : formatGhs(costGhs)}
              </LossCaseValueEm>
            </LossCaseItem>
            <LossCaseItem>
              <OtherText grey>OWNER</OtherText>
              <LossCaseValueEm>{loss.owner}</LossCaseValueEm>
            </LossCaseItem>
            <LossCaseItem>
              <OtherText grey>CLASSIFICATION</OtherText>
              <LossCaseValueEm>{loss.classification}</LossCaseValueEm>
            </LossCaseItem>
            <LossCaseItem>
              <OtherText grey>ROOT CAUSE</OtherText>
              <LossCaseValueEm>{fuelLossRootCauseLabel(lossApi.rootCause)}</LossCaseValueEm>
            </LossCaseItem>
            {loss.closedBy && (
              <LossCaseItem>
                <OtherText grey>CLOSED BY</OtherText>
                <LossCaseValueEm>{loss.closedBy}</LossCaseValueEm>
              </LossCaseItem>
            )}
          </LossCaseGrid>

          {caseStatus === 'OPEN' && (
            <LossClosureWrap>
              <OtherText grey>ASSIGN OWNER</OtherText>
              <BodyText color={tokens.text.muted}>
                Assigning starts the investigation and moves the case to Under investigation.
              </BodyText>
              <HonestSelectAsyncInput
                link='users/basic'
                name='owner'
                labelText='Owner'
                defaultValue={owner}
                onChange={(v: ISelectValue) => setOwner(v)}
                parserFunction={parseUsers}
                placeholder='Select owner…'
              />
              <Button variant='primary' size='sm' disabled={!owner?.value || busy} onClick={handleAssign}>
                Assign & start investigation
              </Button>
            </LossClosureWrap>
          )}

          {(caseStatus === 'UNDER_INVESTIGATION' || caseStatus === 'PENDING_APPROVAL') && (
            <LossSection>
              <OtherText grey>ADD NOTE</OtherText>
              <Input
                type='textarea'
                height='80px'
                nomarg
                labelText='Investigation note'
                placeholder='What was checked…'
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <Button
                variant='secondary'
                size='sm'
                disabled={!noteText.trim() || busy}
                onClick={handleAddNote}
              >
                Append note
              </Button>
            </LossSection>
          )}

          <LossSection>
            <OtherText grey>CASE NOTES</OtherText>
            {!detail.notes?.length ? (
              <OtherText color={tokens.text.muted}>No notes yet</OtherText>
            ) : (
              <LossScrollList>
                {detail.notes.map((n) => (
                  <LossNoteCard key={n.id}>
                    <BodyText>{n.content}</BodyText>
                    <OtherText color={tokens.text.muted}>
                      {`${formatFuelUserName(n.author)} · ${formatDateWithTime(n.createdAt)}`}
                    </OtherText>
                  </LossNoteCard>
                ))}
              </LossScrollList>
            )}
          </LossSection>

          <LossSection>
            <OtherText grey>AUDIT TRAIL</OtherText>
            {!detail.auditTrail?.length ? (
              <OtherText color={tokens.text.muted}>No audit entries</OtherText>
            ) : (
              <LossScrollList>
                {detail.auditTrail.map((a) => (
                  <LossNoteCard key={a.id}>
                    <BodyText>{fuelLossAuditEventLabel(Number(a.eventType))}</BodyText>
                    <OtherText color={tokens.text.muted}>
                      {`${formatFuelUserName(a.actionBy) === '—' ? 'System' : formatFuelUserName(a.actionBy)} · ${formatDateWithTime(a.createdAt)}`}
                    </OtherText>
                  </LossNoteCard>
                ))}
              </LossScrollList>
            )}
          </LossSection>

          <LossClosureWrap>
            <OtherText grey>CLOSURE</OtherText>
            {isClosed && (
              <>
                <BodyText>
                  {`Closed ${loss.closedAt || '—'} — outcome ${loss.outcome ? OUTCOME_META[loss.outcome].label.toLowerCase() : '—'}`}
                </BodyText>
                {loss.approval?.approvedBy && (
                  <OtherText color={tokens.status.success.fg}>
                    {`Write-off approved by ${loss.approval.approvedBy} · ${loss.approval.approvedAt || '—'}`}
                  </OtherText>
                )}
                {!loss.closedBy && loss.outcome === 'WRITTEN_OFF' && (
                  <OtherText color={tokens.text.muted}>Auto-closed by the system</OtherText>
                )}
              </>
            )}

            {caseStatus === 'UNDER_INVESTIGATION' && (
              <>
                <Flex gap='1rem' width='100%'>
                  <HonestSelectInput
                    options={fuelLossOutcomes}
                    defaultValue={outcome}
                    labelText='Outcome'
                    onChange={(v: ISelectValue) => setOutcome(v)}
                  />
                  <HonestSelectInput
                    options={fuelLossRootCauses}
                    defaultValue={rootCause}
                    labelText='Root cause'
                    onChange={(v: ISelectValue) => setRootCause(v)}
                  />
                </Flex>
                <Input
                  type='textarea'
                  height='70px'
                  nomarg
                  labelText='Closure summary (optional)'
                  value={closureNote}
                  onChange={(e) => setClosureNote(e.target.value)}
                />
                <Flex gap='0.5rem' wrap>
                  {(Number(outcome?.value) !== FuelLossOutcome.WrittenOff || !approvalRequired) && (
                    <Button
                      variant='primary'
                      size='sm'
                      disabled={!outcome?.value || !rootCause?.value || busy}
                      onClick={() => handleClose()}
                    >
                      Close case
                    </Button>
                  )}
                  {Number(outcome?.value) === FuelLossOutcome.WrittenOff && approvalRequired && (
                    <Button
                      variant='danger'
                      size='sm'
                      disabled={!rootCause?.value || busy}
                      onClick={handleSendForApproval}
                    >
                      {`Send for ${loss.approval?.approverRole || 'GM'} approval`}
                    </Button>
                  )}
                </Flex>
                {approvalRequired && (
                  <OtherText color={tokens.text.muted}>
                    {`Write-offs above ${formatGhs(threshold)} require GM approval before closing.`}
                  </OtherText>
                )}
              </>
            )}

            {caseStatus === 'PENDING_APPROVAL' && (
              <LossApprovalBanner>
                <BodyText color={tokens.status.warning.fg}>
                  {approved
                    ? `Write-off approved by ${loss.approval?.approvedBy} — confirm closure to write off ${formatGhs(costGhs)}`
                    : `Write-off ${formatGhs(costGhs)} exceeds ${formatGhs(threshold)} — requires ${loss.approval?.approverRole || 'GM'} approval`}
                </BodyText>
                <Flex gap='0.5rem' wrap>
                  {!approved && (
                    <Button variant='danger' size='sm' disabled={busy} onClick={handleApprove}>
                      Approve write-off (GM)
                    </Button>
                  )}
                  {approved && (
                    <Button
                      variant='danger'
                      size='sm'
                      disabled={busy}
                      onClick={() =>
                        handleClose(FuelLossOutcome.WrittenOff)
                      }
                    >
                      Confirm write-off
                    </Button>
                  )}
                </Flex>
                {!approved && (
                  <>
                    <Input
                      type='textarea'
                      height='70px'
                      nomarg
                      labelText='Reject reason'
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                    <Button
                      variant='secondary'
                      size='sm'
                      disabled={!rejectReason.trim() || busy}
                      onClick={handleReject}
                    >
                      Reject (return to investigation)
                    </Button>
                  </>
                )}
              </LossApprovalBanner>
            )}
          </LossClosureWrap>
        </LossModalBody>
      </Flex>
    </Modal>
  )
}
